// Versão: 2.02
// Data..: 2026-09-26
// v2.02: página em .full-bleed (ocupa toda a largura do painel, sem limite de 1600px).
// Minha Central do técnico: chamados atribuídos, tarefas, seguidos, vencendo hoje
// e aprovações pendentes. v2.00: personalizável (mostrar/ocultar/reordenar widgets),
// preferência salva no navegador (localStorage).
// v2.01: linha inteira do chamado clicável para abrir o detalhe.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApprovalStatusLabels, type MyCentralData, type Ticket, type Approval } from '@makweb/shared';
import { api } from '../lib/api.js';
import { PageHeader } from '../components/PageHeader.js';
import { StatusBadge, PriorityBadge } from '../components/Badge.js';

// Identificadores estáveis de cada widget da central.
type WidgetId = 'stats' | 'dueToday' | 'approvals' | 'assigned' | 'tasks' | 'following';

interface WidgetPref { id: WidgetId; visible: boolean; }

const WIDGET_LABELS: Record<WidgetId, string> = {
  stats: 'Indicadores (cartões)',
  dueToday: '⏰ Vencendo hoje',
  approvals: '📝 Aprovações pendentes',
  assigned: '🎯 Atribuídos a mim',
  tasks: '✅ Minhas tarefas',
  following: '👀 Que eu sigo',
};

// Ordem e visibilidade padrão dos widgets.
const DEFAULT_LAYOUT: WidgetPref[] = [
  { id: 'stats', visible: true },
  { id: 'dueToday', visible: true },
  { id: 'approvals', visible: true },
  { id: 'assigned', visible: true },
  { id: 'tasks', visible: true },
  { id: 'following', visible: true },
];

const STORAGE_KEY = 'makweb.central.layout';

// Carrega o layout salvo, garantindo que todos os widgets conhecidos existam
// (tolerante a versões antigas: adiciona widgets novos ao final).
function loadLayout(): WidgetPref[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [...DEFAULT_LAYOUT];
    const saved = JSON.parse(raw) as WidgetPref[];
    const known = new Set(DEFAULT_LAYOUT.map((w) => w.id));
    const result = saved.filter((w) => known.has(w.id));
    for (const def of DEFAULT_LAYOUT) {
      if (!result.some((w) => w.id === def.id)) result.push(def);
    }
    return result;
  } catch {
    return [...DEFAULT_LAYOUT];
  }
}

export function MyCentralPage() {
  const [data, setData] = useState<MyCentralData | null>(null);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState<WidgetPref[]>(() => loadLayout());
  const [customizing, setCustomizing] = useState(false);

  useEffect(() => {
    api.get<MyCentralData>('/me/central').then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  const persist = (next: WidgetPref[]) => {
    setLayout(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* modo privado */ }
  };

  const toggle = (id: WidgetId) => persist(layout.map((w) => (w.id === id ? { ...w, visible: !w.visible } : w)));
  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= layout.length) return;
    const next = [...layout];
    [next[index], next[target]] = [next[target], next[index]];
    persist(next);
  };
  const reset = () => persist([...DEFAULT_LAYOUT]);

  const visibleWidgets = useMemo(() => layout.filter((w) => w.visible), [layout]);

  if (loading) return <div className="flex justify-center" style={{ padding: 48 }}><span className="spinner" /></div>;
  if (!data) return <div className="card empty"><div className="empty-icon">🔒</div>Disponível apenas para a equipe.</div>;

  const renderWidget = (id: WidgetId) => {
    switch (id) {
      case 'stats':
        return (
          <div key="stats" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--s4)', marginBottom: 'var(--s5)' }}>
            <StatCard label="Atribuídos a mim" value={data.assigned.length} icon="🎯" />
            <StatCard label="Minhas tarefas" value={data.tasks.length} icon="✅" />
            <StatCard label="Vencendo hoje" value={data.dueToday.length} icon="⏰" accent={data.dueToday.length > 0} />
            <StatCard label="Aprovações p/ mim" value={data.pendingApprovals.length} icon="📝" accent={data.pendingApprovals.length > 0} />
          </div>
        );
      case 'dueToday':
        return data.dueToday.length > 0 ? <TicketList key="dueToday" title="⏰ Vencendo hoje" tickets={data.dueToday} /> : null;
      case 'approvals':
        return data.pendingApprovals.length > 0 ? (
          <div key="approvals" className="card mb-4">
            <h3>📝 Aprovações pendentes para mim</h3>
            <div className="flex flex-col gap-2 mt-2">
              {data.pendingApprovals.map((a: Approval) => (
                <div key={a.id} className="flex items-center justify-between text-sm">
                  <span>{a.ticketNumber ? <Link to={`/tickets/${a.ticketId}`}>#{a.ticketNumber}</Link> : '—'} · {a.subject}</span>
                  <span className="badge badge-waiting_client">{ApprovalStatusLabels[a.status]}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null;
      case 'assigned':
        return <TicketList key="assigned" title="🎯 Atribuídos a mim" tickets={data.assigned} emptyMsg="Nenhum chamado atribuído a você." />;
      case 'tasks':
        return data.tasks.length > 0 ? <TicketList key="tasks" title="✅ Minhas tarefas" tickets={data.tasks} /> : null;
      case 'following':
        return data.following.length > 0 ? <TicketList key="following" title="👀 Que eu sigo" tickets={data.following} /> : null;
      default:
        return null;
    }
  };

  return (
    <div className="full-bleed">
      <PageHeader
        eyebrow="Atendimento"
        title="Minha Central"
        description="Seu resumo de trabalho: chamados, tarefas, prazos de hoje e aprovações."
        actions={<button className="btn btn-ghost" onClick={() => setCustomizing((v) => !v)}>{customizing ? 'Concluir' : '⚙ Personalizar'}</button>}
      />

      {customizing && (
        <div className="card mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 style={{ margin: 0 }}>Personalizar central</h3>
            <button className="btn btn-sm btn-ghost" onClick={reset}>Restaurar padrão</button>
          </div>
          <p className="text-muted text-sm">Escolha o que exibir e a ordem. As preferências ficam salvas neste navegador.</p>
          <div className="flex flex-col gap-1 mt-2">
            {layout.map((w, i) => (
              <div key={w.id} className="flex items-center justify-between" style={{ padding: '8px 10px', borderRadius: 8, background: 'var(--surface-2)' }}>
                <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
                  <input type="checkbox" checked={w.visible} onChange={() => toggle(w.id)} />
                  <span>{WIDGET_LABELS[w.id]}</span>
                </label>
                <div className="flex gap-1">
                  <button className="btn btn-sm btn-ghost" title="Subir" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                  <button className="btn btn-sm btn-ghost" title="Descer" disabled={i === layout.length - 1} onClick={() => move(i, 1)}>↓</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {visibleWidgets.map((w) => renderWidget(w.id))}
    </div>
  );
}

function StatCard({ label, value, icon, accent }: { label: string; value: number; icon: string; accent?: boolean }) {
  return (
    <div className={`stat-card ${accent ? 'is-warn' : ''}`}>
      <span className="stat-icon">{icon}</span>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

function TicketList({ title, tickets, emptyMsg }: { title: string; tickets: Ticket[]; emptyMsg?: string }) {
  const navigate = useNavigate();
  return (
    <div className="card card-flush mb-4">
      <div style={{ padding: '14px var(--s4) 0' }}><h3>{title}</h3></div>
      {tickets.length === 0 ? (
        <div className="empty"><div className="empty-icon">📭</div>{emptyMsg ?? 'Nada por aqui.'}</div>
      ) : (
        <table className="table table-clickable">
          <thead><tr><th>#</th><th>Assunto</th><th>Status</th><th>Prioridade</th><th>Prazo</th></tr></thead>
          <tbody>
            {tickets.map((t) => (
              // Linha inteira clicável: abre o chamado. Mantém o link no # para
              // permitir abrir em nova aba (Ctrl/⌘ + clique).
              <tr key={t.id} onClick={() => navigate(`/tickets/${t.id}`)}>
                <td onClick={(e) => e.stopPropagation()}><Link to={`/tickets/${t.id}`}>#{t.number}</Link></td>
                <td className="font-semibold">{t.subject}</td>
                <td><StatusBadge status={t.status} /></td>
                <td><PriorityBadge priority={t.priority} /></td>
                <td className="text-muted text-sm">{(t.resolutionDueAt ?? t.dueAt) ? new Date((t.resolutionDueAt ?? t.dueAt)!).toLocaleString('pt-BR') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
